import { useToast } from "@aircall/tractor";
import { useLazyQuery } from "@apollo/client";
import {
  BACKEND_ERRORS_TRANSLATION_KEYS,
  ERROR_TYPES,
} from "@dashboard/library";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import {
  AudioType,
  CallAudio,
  GetAudioQuery,
  GetAudioQueryVariables,
} from "@/graphql/generated/graphql";
import { GetAudio } from "@/graphql/queries/GetAudio";
import { useQueryWrapper } from "@/hooks/useQueryWrapper/useQueryWrapper";
import { PathParams } from "@/states/path_param/path_param.decl";
import { useBaseStore } from "@/store/store";
import { QueryOptions, UseAudioQuery } from "./useAudioUrlQuery.decl";

/**
 * The hook responsible for querying from server the audio source of the provided call id.
 * In case of an issue the hook will notify the user on a failure in the request.
 * @param options
 */
export const useAudioUrlQuery = (options: QueryOptions = {}): UseAudioQuery => {
  const setAudio = useBaseStore((state) => state.setAudio);

  const { callId, callType } = useParams<PathParams>() as PathParams;

  const audioType: GetAudioQueryVariables["callType"] =
    callType === "voicemail" ? AudioType.VOICEMAIL : AudioType.CONFERENCE;

  const { data, refetch, loading, error, networkStatus } = useQueryWrapper<
    GetAudioQuery,
    GetAudioQueryVariables
  >(GetAudio, {
    variables: { callId, callType: audioType },
    onCompleted(data) {
      if (data.getAudio?.__typename === "CallAudio") {
        setAudio(data.getAudio);
      }
    },
    ...options,
  });

  let formedError = error;
  if (
    data?.getAudio?.__typename === "InsufficientPermissionException" ||
    data?.getAudio?.__typename === "ResourceNotFoundException" ||
    data?.getAudio?.__typename === "GenericException"
  ) {
    formedError = {
      type: ERROR_TYPES.UNAUTHORIZED,
      message: `${data.getAudio.message} code:${data.getAudio.code} `,
      errorI18nKey: BACKEND_ERRORS_TRANSLATION_KEYS.UNKNOWN_ERROR,
    };
  }

  return {
    audioUrl:
      data?.getAudio?.__typename === "CallAudio"
        ? data.getAudio.url
        : undefined,
    refetch,
    loading,
    error: formedError,
    networkStatus,
  };
};

/**
 * The hook responsible for providing a function that will query the audio source.
 * This is usefully when the source is needed when triggering event (e.g. onClick)
 */
export const useAudioUrlLazyQuery = () => {
  const { callId, callType } = useParams<PathParams>() as PathParams;
  const { t } = useTranslation();
  const audioType: GetAudioQueryVariables["callType"] =
    callType === "voicemail" ? AudioType.VOICEMAIL : AudioType.CONFERENCE;
  const { showToast } = useToast();

  const [query] = useLazyQuery<GetAudioQuery, GetAudioQueryVariables>(
    GetAudio,
    {
      variables: { callId, callType: audioType },
      fetchPolicy: "no-cache",
      onError: () =>
        showToast({
          variant: "critical",
          message: t("toast.fetch_failure"),
        }),
    }
  );

  const queryAudioUrl = useCallback(async () => {
    const { data } = await query();
    if (data?.getAudio?.__typename === "CallAudio") {
      const audio = data.getAudio as CallAudio;
      return audio.url;
    }
  }, [query]);

  return { queryAudioUrl };
};
